import type { FC } from 'react';
import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  memo,
  useMemo,
} from 'react';
import { Stack, Tooltip, Typography } from '@mui/material';
import type { CashTransactionGroupRowModel } from './model';
import { Iconify } from '@pflegenavi/web-components';
import { CashTransactionGroupType } from '@pflegenavi/shared/api';
import { useTranslation } from 'react-i18next';

interface DateColumnProps {
  row: Pick<
    CashTransactionGroupRowModel,
    'lastUpdateDateString' | 'lastUpdateTime'
  >;
}

export const DateColumn: FC<DateColumnProps> = ({ row }) => {
  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        {row.lastUpdateDateString}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          lineHeight: 1,
        }}
      >
        {row.lastUpdateTime}
      </Typography>
    </Stack>
  );
};

interface CashAmountColumnProps {
  row: Pick<
    CashTransactionGroupRowModel,
    | 'transferCount'
    | 'transferAmount'
    | 'transferColor'
    | 'separateFeeAmountString'
    | 'separateFeeAmountCents'
    | 'immediateFeeAmountString'
    | 'immediateFeeAmountCents'
    | 'immediateFeeAmountColor'
  >;
}

export const CashAmountColumn: FC<CashAmountColumnProps> = ({ row }) => {
  const { t } = useTranslation();
  const cellContent = (
    <Stack
      direction="column"
      sx={{
        gap: 1,
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: 0.5,
        }}
      >
        <Typography
          variant="body2"
          color={row.transferColor}
          className={'cashAmountColumn'}
          sx={{
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {row.transferAmount}
        </Typography>
        {row.immediateFeeAmountCents !== 0 && (
          <Typography
            variant="body2"
            color={row.immediateFeeAmountColor}
            className={'cashAmountColumn'}
            sx={{
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            {`(${row.immediateFeeAmountString})`}
          </Typography>
        )}
        {row.separateFeeAmountCents !== 0 && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            {'*'}
          </Typography>
        )}
      </Stack>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          lineHeight: 1,
        }}
      >
        {row.transferCount}
      </Typography>
    </Stack>
  );

  return row.separateFeeAmountCents !== 0 ||
    row.immediateFeeAmountCents !== 0 ? (
    <Tooltip
      arrow
      placement={'right'}
      title={t('cashManagement.link.tooltip.amount', {
        separateFeeAmount: row.separateFeeAmountString,
        immediateFeeAmount: row.immediateFeeAmountString,
      })}
    >
      {cellContent}
    </Tooltip>
  ) : (
    cellContent
  );
};

interface LinkAmountColumnProps {
  row: Pick<
    CashTransactionGroupRowModel,
    | 'linkCount'
    | 'amountLinkedString'
    | 'separateFeeAmountLinkedString'
    | 'separateFeeAmountLinkedCents'
    | 'immediateFeeAmountLinkedString'
    | 'immediateFeeAmountLinkedCents'
  >;
}

export const LinkAmountColumn: FC<LinkAmountColumnProps> = ({ row }) => {
  const { t } = useTranslation();

  const cellContent = (
    <Stack
      direction="column"
      sx={{
        gap: 1,
      }}
    >
      <Stack
        direction="row"
        sx={{
          gap: 0.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {row.amountLinkedString}
        </Typography>
        {row.immediateFeeAmountLinkedCents !== 0 && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            {`(${row.immediateFeeAmountLinkedString})`}
          </Typography>
        )}
        {row.separateFeeAmountLinkedCents !== 0 && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            {'*'}
          </Typography>
        )}
      </Stack>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          lineHeight: 1,
        }}
      >
        {row.linkCount}
      </Typography>
    </Stack>
  );

  return row.separateFeeAmountLinkedCents !== 0 ||
    row.immediateFeeAmountLinkedCents !== 0 ? (
    <Tooltip
      arrow
      placement={'right'}
      title={t('cashManagement.link.tooltip.amount-linked', {
        separateFeeAmount: row.separateFeeAmountLinkedString,
        immediateFeeAmount: row.immediateFeeAmountLinkedString,
      })}
    >
      {cellContent}
    </Tooltip>
  ) : (
    cellContent
  );
};

export const IconCheckmark: FC = () => {
  return (
    <Iconify
      width={20}
      height={20}
      icon="eva:checkmark-circle-2-fill"
      color="success.main"
    />
  );
};

export const IconConfirmed: FC = () => {
  return (
    <Iconify
      width={20}
      height={20}
      icon="eva:done-all-outline"
      color="success.main"
    />
  );
};

export const IconNeutralCheckmark: FC = () => {
  return (
    <Iconify
      width={20}
      height={20}
      icon="eva:checkmark-circle-2-outline"
      color="success.main"
    />
  );
};

export const IconWarning: FC = () => {
  return (
    <Iconify
      width={20}
      height={20}
      icon="eva:alert-circle-fill"
      color="warning.main"
    />
  );
};

export const IconExpense: FC<{ mismatch: boolean }> = ({ mismatch }) => {
  return (
    <Iconify
      width={20}
      height={20}
      icon="tabler:receipt-euro"
      color={mismatch ? 'warning.main' : 'success.main'}
      data-testid={mismatch ? 'expense-icon-mismatch' : 'expense-icon-match'}
    />
  );
};

export interface CheckColumnProps {
  row: Pick<
    CashTransactionGroupRowModel,
    | 'mismatchConfirmation'
    | 'amountLinked'
    | 'cashChanged'
    | 'type'
    | 'expensePartCount'
  >;
}

export const CheckColumn: React.FC<CheckColumnProps> = memo(({ row }) => {
  const [animate, setAnimate] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true); // set to true on initial mount
  }, []);

  const icon = useMemo(() => {
    const amountLinkedOr0 = row.amountLinked ?? 0;
    const cashChangedOr0 = row.cashChanged ?? 0;

    if (row.expensePartCount > 0) {
      return <IconExpense mismatch={amountLinkedOr0 !== cashChangedOr0} />;
    }

    if (row.mismatchConfirmation) {
      return <IconConfirmed />;
    }

    switch (row.type) {
      case CashTransactionGroupType.Adjustment:
      case CashTransactionGroupType.Deposit: // No way to link a deposit atm
        return <IconNeutralCheckmark />;
    }

    if (amountLinkedOr0 === cashChangedOr0) {
      return <IconCheckmark />;
    }

    return <IconWarning />;
  }, [
    row.expensePartCount,
    row.mismatchConfirmation,
    row.amountLinked,
    row.cashChanged,
    row.type,
  ]);

  const prevIcon = useRef(icon);

  useEffect(() => {
    setIsMounted(true); // set to true on initial mount
  }, []);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isMounted && prevIcon.current !== icon) {
      setAnimate(true);
      const timer = setTimeout(() => {
        setAnimate(false);
      }, 200);

      return () => {
        clearTimeout(timer);
      };
    }
    prevIcon.current = icon; // update the previous check value
  }, [isMounted, icon]);

  return (
    <Stack
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        transition: 'all 0.2s ease',
        transform: animate ? 'scale(1.4)' : 'scale(1)',
      }}
    >
      {icon}
    </Stack>
  );
});

interface NotesColumnProps {
  row: Pick<CashTransactionGroupRowModel, 'notes'>;
}

// Clamped to two 20px lines so a two-line note is no taller than the 40px type
// avatar; the full text is in the tooltip once it is cut off.
export const InfoColumn: FC<NotesColumnProps> = ({ row }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [clipped, setClipped] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    setClipped(Boolean(el && el.scrollHeight > el.clientHeight));
  }, [row.notes]);

  return (
    <Tooltip arrow placement="top-start" title={clipped ? row.notes : ''}>
      <Typography
        ref={ref}
        variant="body2"
        sx={{
          lineHeight: '20px',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          overflowWrap: 'anywhere',
        }}
      >
        {row.notes}
      </Typography>
    </Tooltip>
  );
};
